import {
  makeStyles,
  Alert,
  Collapse,
  IconButton,
  ConfirmationDialog,
  Grid,
  CheckIcon,
  Checkbox,
  FormControlLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputField,
  useBreakpoint,
  useTranslation
} from '@paintscout/ui'
import type { DialogProps } from '@paintscout/ui'
import {
  SingleDatePicker,
  KeypadInputField,
  DropdownSelect,
  useDialogs,
  useUser,
  useClientOptions
} from '@paintscout/ui-business'
import {
  formatCurrency,
  getFeature,
  getPaymentMethodOptions,
  getPayments,
  getQuoteOptions,
  hasIntegrationInterface,
  requestHasProcessingPayment,
  setPayments,
  uuid
} from '@paintscout/util/builder'
import moment from 'moment'
import type { Payment, PayrixPaymentType, QuoteDocument } from 'paintscout'
import React, { useState, useRef } from 'react'
import SendIcon from '@material-ui/icons/Send'
import { useQuoteMutations } from 'shared/hooks'
import { useQuoteQuery } from '@paintscout/api'
import { useSnackbar } from 'notistack'
import { isLooselyEqual } from '@paintscout/util'
import { parseNumber } from '@paintscout/util/calculator'
import CloseIcon from '@material-ui/icons/Close'
import SendPaymentRequestDialog from '../SendPaymentRequestDialog'
import TileListDialog from '../TileListDialog'
import CollectPayrixPaymentDialog from '../CollectPayrixPaymentDialog'
const CollectStripePaymentDialog = React.lazy(() => import('../CollectStripePaymentDialog'))
import PaymentStatusBadge from '../../Quote/views/QuoteView/PaymentsTable/PaymentStatusBadge'
import { useQuote } from '../../context/useQuote'
import { useRefreshQuoteFromServer } from '../../util/useRefreshQuoteFromServer'

export interface PaymentsDialogProps extends DialogProps {
  quote?: QuoteDocument // Deals dont have full quote passed in so we search our own by id instead
  quoteId?: string
  paymentIndex?: number
  backOnCancel?: boolean
  markingPaid?: boolean
  onDeal?: boolean
  canDelete?: boolean
  hideDeposit?: boolean
  onConfirm?: (quote: QuoteDocument) => void
  onCancel?: () => void
  /**
   * Called instead of saving once the form is valid. Lets a read-only host (the Painter View
   * sales demo) show the real form without any write reaching the quote.
   */
  onSaveIntercept?: () => void
}

export interface PaymentsDialogState {
  existing: boolean
  payment: Payment
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateRows: '1fr',
    gridGap: theme.spacing()
  },
  buttonContainer: {
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    '&:hover': {
      '& span': {
        color: `${theme.palette.error.main}`
      },
      color: theme.palette.error.main
    }
  },
  markPaidButton: {
    backgroundColor: theme.paintscout.palette.semantic.Success.S60,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem',
      height: '40px'
    }
  },
  resendButton: { color: theme.palette.text.secondary, border: `1px solid ${theme.palette.text.hint}` },
  dialogContent: {
    '& .MuiDialogContent-root': {
      paddingBottom: `${theme.spacing(3)}px !important`
    }
  }
}))

// A cleared input coerces to 0, so 0 is never a valid payment amount; negatives are
// only valid where allowNegative is passed (manual payments with the feature enabled).
function isAmountInvalid(amount: number | string, allowNegative: boolean): boolean {
  const value = parseNumber(amount)
  return allowNegative ? value === 0 : !(value > 0)
}

function PaymentsDialog(props: PaymentsDialogProps) {
  const {
    open,
    paymentIndex,
    markingPaid,
    onConfirm,
    onCancel,
    quote,
    quoteId,
    onDeal,
    backOnCancel,
    canDelete = true,
    hideDeposit = false,
    onSaveIntercept,
    ...baseDialogProps
  } = props
  const { __ } = useTranslation()
  const { dismissDialog, dismissAllDialogs, openDialog } = useDialogs()
  const { options, clientId } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const { saveQuote } = useQuoteMutations()
  const { user, preferences, isPainter } = useUser()
  const { updateQuote } = useQuote()
  const refreshQuoteFromServer = useRefreshQuoteFromServer()
  const classes = useStyles(props)
  const desktop = useBreakpoint((breakpoints) => breakpoints.up('md'))
  const { loading, error } = useQuoteQuery({
    skip: !quoteId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { id: quoteId },
    onCompleted: (data) => {
      setFullDoc(data.quote as QuoteDocument)
    }
  })
  const { allowPaymentsOnInvoices, allowACHPaymentsOnInvoices } = getQuoteOptions({ quote, options })
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada' && hasPayrix
  const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'

  const hasPaymentProcessing = hasStripe || hasStripeW1d || hasPayrix
  const isPaymentEligible = quote.totals.after_tax > 0 && quote.totals.balance_due > 0

  const canCollectCCPayment = hasPaymentProcessing && allowPaymentsOnInvoices && isPaymentEligible
  const canCollectACHPayment = hasPayrix && allowACHPaymentsOnInvoices && isPaymentEligible

  const [fullDoc, setFullDoc] = useState<QuoteDocument>(quote)
  const [isLoading, setIsLoading] = useState(false)
  const [validationError, setValidationError] = useState(false)
  const [amountError, setAmountError] = useState(false)
  const existingPayment = typeof paymentIndex !== 'undefined' && getPayments({ quote: fullDoc })[paymentIndex]
  const isRequest = !!existingPayment?.id && existingPayment.status === 'pending'

  // A pending request can spawn a payment when the customer pays through the portal. While that
  // payment is still processing, editing/deleting the request would conflict with the in-flight
  // payment, so we lock the form until it resolves.
  const hasProcessingPayment =
    !!existingPayment && requestHasProcessingPayment({ quote: fullDoc, payment: existingPayment })

  const initialPayment: Payment =
    markingPaid || existingPayment
      ? {
          ...existingPayment,
          ...(markingPaid
            ? {
                date: Date.now()
              }
            : {})
        }
      : {
          id: uuid(),
          type: null,
          amount: fullDoc?.totals?.balance_due,
          date: Date.now(),
          note: '',
          deposit: false,
          manual: !isRequest,
          status: 'paid'
        }

  const initialPaymentRef = useRef<Payment>(initialPayment)
  const hasUserChangesRef = useRef(false)
  const [payment, setPayment] = useState<Payment>(initialPayment)

  // Marking a request paid for a different amount than requested effectively changes the request
  // amount and closes it in one motion — paid requests are hidden from the table, so it "disappears".
  // This is intended behaviour, so we just warn rather than block.
  const requestedAmount = parseNumber(initialPaymentRef.current.amount)
  const enteredAmount = parseNumber(payment.amount)
  const isAmountMismatchMarkPaid = !!markingPaid && enteredAmount > 0 && enteredAmount !== requestedAmount

  // A request whose amount exceeds the balance due is hidden from the customer, so resending it is
  // pointless. The PaymentsTable action menu hides "Resend" in this case; here in the dialog we grey
  // out the button instead. Matches the over-requested gate on the table's `showResend`.
  const balanceDue = fullDoc?.totals?.balance_due ?? quote?.totals?.balance_due ?? 0
  const allowNegativeNumberInputs = getFeature({ options, preferences, path: 'allowNegativeNumberInputs' })
  // Negative amounts are only ever valid for manually-recorded payments. Requests (and marking one
  // paid) feed the customer portal and payment processors, which must never see a negative amount.
  const allowNegativeAmount = allowNegativeNumberInputs && !isRequest
  const isOverRequested = enteredAmount > balanceDue

  const dateValue = moment(payment.date)
  const typeOptions = getPaymentMethodOptions(options).map((option) => ({
    ...option,
    value: option.label
  }))
  const typeValue = payment.type
    ? {
        key: payment.type,
        label: payment.type,
        value: payment.type
      }
    : null

  const leftButton = markingPaid ? (
    <Button onClick={handleClose} variant="textNoPadding">
      {backOnCancel ? __('< Back') : __('Cancel')}
    </Button>
  ) : !existingPayment || !canDelete ? (
    <></>
  ) : (
    <Button
      onClick={handleDeleteClick}
      loading={isLoading}
      disabled={!!loading || !!error || !fullDoc || isLoading || hasProcessingPayment}
      variant="textNoPadding"
      className={classes.deleteButton}
    >
      {__('Delete')}
    </Button>
  )

  const defaultTitle = existingPayment ? __('Edit Payment Request') : __('Add Payment')
  const title = markingPaid ? __('Mark as Successful') : defaultTitle

  function hasChanges(): boolean {
    if (hasUserChangesRef.current) {
      return true
    }
    // Normalize payment objects for comparison (handle date as unix timestamp)
    const currentPayment = {
      ...payment,
      date: typeof payment.date === 'number' ? payment.date : moment(payment.date).valueOf()
    }
    const initialPaymentForComparison = {
      ...initialPaymentRef.current,
      date:
        typeof initialPaymentRef.current.date === 'number'
          ? initialPaymentRef.current.date
          : moment(initialPaymentRef.current.date).valueOf()
    }
    return !isLooselyEqual(currentPayment, initialPaymentForComparison)
  }

  function handleClose() {
    if (hasChanges()) {
      openDialog(ConfirmationDialog, {
        message: __('You have unsaved changes. Are you sure you want to close?'),
        onConfirm: () => {
          dismissDialog() // Dismiss confirmation dialog
          dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      if (onCancel) {
        onCancel()
      }
      dismissAllDialogs()
    }
  }

  return (
    <Dialog
      classes={{ paper: classes.dialogContent }}
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      {...baseDialogProps}
    >
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={handleClose} disabled={isLoading || loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <div className={classes.root}>
          {hasProcessingPayment && (
            <PaymentStatusBadge
              status="processing"
              message={__("This request has a payment that is processing and can't be edited until it resolves.")}
            />
          )}
          {(!isRequest || markingPaid) && (
            <DropdownSelect
              variant="single"
              value={typeValue}
              label={__('Type/Method')}
              fullWidth={true}
              name={'type'}
              disabled={hasProcessingPayment}
              onChange={handleTypeChange}
              options={typeOptions}
              searchable
              placeholder={{
                selectPlaceholder: __('Select a Payment Method'),
                plural: __('Payment Methods')
              }}
              popperModifiers={{
                flip: {
                  enabled: false
                },
                preventOverflow: {
                  enabled: false
                }
              }}
              // The dialog content scrolls (so Save stays pinned in a short window, ENG-2234), which
              // would clip an inline popper. Fixed positioning escapes that clip while the popper stays
              // inside the dialog DOM, so the focus trap still lets you type in the search box.
              // Below md, keep the inline popper tuned for phones (ENG-2158).
              positionFixed={desktop}
              required
            />
          )}

          <Collapse show={validationError}>
            <Alert severity="error" noMargin>
              {__('Please select a payment method')}
            </Alert>
          </Collapse>
          <KeypadInputField
            required
            label={__('Amount')}
            value={payment.amount}
            fullWidth={true}
            format={'price'}
            name={'amount'}
            allowNegative={allowNegativeAmount}
            disabled={hasProcessingPayment}
            onChange={handleKeypadChange}
          />
          <Collapse show={amountError}>
            <Alert severity="error" noMargin>
              {allowNegativeAmount
                ? __('Please enter an amount other than %s', formatCurrency({ options, value: 0 }))
                : __('Please enter an amount greater than %s', formatCurrency({ options, value: 0 }))}
            </Alert>
          </Collapse>
          <Collapse show={isAmountMismatchMarkPaid}>
            <Alert severity="warning" noMargin>
              {__(
                'This is %s than the requested amount of %s. Marking it as paid will update the request amount to match.',
                enteredAmount < requestedAmount ? __('less') : __('more'),
                formatCurrency({ options, value: requestedAmount })
              )}
            </Alert>
          </Collapse>
          <SingleDatePicker
            required
            label={isRequest && !markingPaid ? __('Date Sent') : __('Payment Date')}
            date={dateValue}
            disabled={hasProcessingPayment}
            onChange={handleDateChange}
            // Same reason as the Type/Method dropdown: escape the scrolling content without a portal.
            // The desktop popper only exists at md and up; below that the picker opens as a modal.
            PopperProps={{ popperOptions: { positionFixed: true } }}
          />

          <InputField
            label={__('Client Note')}
            value={payment.note}
            multiline={true}
            maxRows={4}
            name={'note'}
            fullWidth={true}
            disabled={hasProcessingPayment}
            onChange={handleChange}
          />
          {!isRequest && !hideDeposit && (
            <FormControlLabel
              label={__('This payment is a deposit')}
              control={<Checkbox name={'deposit'} checked={payment.deposit} onChange={handleCheckboxChange} />}
            />
          )}
          {isRequest && !markingPaid && (
            <Grid container spacing={2} justifyContent="space-between" className={classes.buttonContainer}>
              {/* Painters can't request payments (no request action on the payments screen), so
                  they shouldn't be able to resend a payment request either — hide Resend for them
                  and let "Mark As Successful" fill the row. See ENG-757. */}
              {!isPainter && (
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="dialog"
                    className={classes.resendButton}
                    aria-label={__('Resend')}
                    onClick={handleResend}
                    disabled={hasProcessingPayment || isOverRequested}
                    icon={SendIcon}
                  >
                    {__('Resend')}
                  </Button>
                </Grid>
              )}
              <Grid item xs={isPainter ? 12 : 6}>
                <Button
                  fullWidth
                  className={classes.markPaidButton}
                  aria-label={__('Mark As Successful')}
                  onClick={handleMarkAsPaid}
                  disabled={hasProcessingPayment}
                  icon={CheckIcon}
                >
                  {__('Mark As Successful')}
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      </DialogContent>
      <DialogActions leftButton={leftButton}>
        <Button
          type="submit"
          variant={'contained'}
          onClick={handleConfirm}
          loading={isLoading || loading}
          disabled={validationError || amountError || !!error || !fullDoc || hasProcessingPayment}
        >
          {__('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )

  function handleChange(event: any) {
    const { value, name, type } = event.target
    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      [name]: type === 'number' ? Number(value) : value
    })
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    const { name } = event.target

    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      [name]: !!checked
    })
  }

  function handleKeypadChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target

    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      [name]: Number(value)
    })
    if (name === 'amount') {
      setAmountError(false)
    }
  }

  function handleDateChange(value: moment.Moment) {
    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      date: value?.unix() * 1000
    })
  }

  function handleTypeChange(value: any) {
    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      type: value?.value
    })
    setValidationError(false)
  }

  function handleResend() {
    openDialog(SendPaymentRequestDialog, {
      payment,
      user,
      preferences,
      isResend: true,
      // On a deal there's no QuoteContext for SendPaymentRequestDialog to read the quote/save from,
      // so pass the quote explicitly and save via the mutation + onConfirm path instead.
      ...(onDeal ? { quote: fullDoc, onConfirm } : {})
    })
  }

  function handleMarkAsPaid() {
    openDialog(TileListDialog, {
      title: __('Mark as Successful'),
      showTopClose: true,
      items: [
        {
          key: 'record-payment',
          label: __('Record Payment')
        },
        canCollectCCPayment && {
          key: 'credit',
          label: __('Take Credit Card Payment')
        },
        canCollectACHPayment && {
          key: 'ach',
          label: __('Take %s Payment', achLabel)
        }
      ].filter(Boolean),
      onItemSelect: (key: string) => {
        if (key === 'record-payment') {
          openDialog(PaymentsDialog, {
            quote: fullDoc,
            paymentIndex,
            backOnCancel: true,
            markingPaid: true,
            onConfirm: async (quote) => {
              if (onDeal) {
                const { quote: savedQuote } = await saveQuote({
                  quote: {
                    ...quote,
                    dates: {
                      ...quote.dates,
                      updated: Date.now()
                    }
                  }
                })
                onConfirm(savedQuote)
              } else {
                if (onConfirm) {
                  onConfirm(quote)
                }
              }
              dismissAllDialogs()
            },
            onCancel: dismissDialog
          })
        } else {
          handleCollectPayment({
            amount: payment.amount,
            note: payment.note,
            deposit: payment.deposit,
            paymentRequestId: payment.id,
            paymentType: key as PayrixPaymentType
          })
        }
      }
    })
  }

  function handleCollectPayment({
    amount,
    note,
    deposit,
    paymentRequestId,
    paymentType
  }: {
    amount: number
    note: string
    deposit: boolean
    paymentRequestId?: string
    paymentType?: PayrixPaymentType
  }) {
    // Never hand a non-positive amount to a payment processor, regardless of the
    // allowNegativeNumberInputs feature (which only applies to manually-recorded payments).
    // Snackbar rather than the inline alert: this fires from the Mark-as-Successful tile
    // dialog, where the alert isn't visible, and the alert's copy varies with the feature.
    if (isAmountInvalid(amount, false)) {
      enqueueSnackbar(__('Please enter an amount greater than %s', formatCurrency({ options, value: 0 })), {
        variant: 'error'
      })
      return
    }
    if (!hasPaymentProcessing) {
      enqueueSnackbar(
        __('Something is wrong with your payment integration. Please contact our support team for assistance.'),
        { variant: 'error' }
      )
      dismissAllDialogs()
      return
    }
    const PaymentDialog = hasPayrix ? CollectPayrixPaymentDialog : CollectStripePaymentDialog
    openDialog(PaymentDialog, {
      quote: fullDoc,
      amount,
      note,
      options,
      companyId: clientId,
      isDeposit: deposit,
      source: 'collect-payment',
      paymentRequestId,
      paymentType,
      backOnCancel: true,
      onCancel: dismissDialog,
      onConfirm: async (updatedQuote: QuoteDocument) => {
        dismissAllDialogs()
        if (onDeal) {
          const { quote: savedQuote } = await saveQuote({
            quote: {
              ...updatedQuote,
              dates: {
                ...updatedQuote.dates,
                updated: Date.now()
              }
            }
          })
          onConfirm(savedQuote)
        } else {
          await updateQuote({ quote: updatedQuote, autosave: false, blockDirty: true })
        }
      },
      // A deal has no QuoteContext to refresh; EditDeal reloads its invoices itself.
      onPaymentFailed: onDeal ? undefined : refreshQuoteFromServer
    })
  }

  function handleDeleteClick() {
    openDialog(ConfirmationDialog, {
      message:
        payment?.status === 'pending'
          ? __('Are you sure you want to remove this payment request?')
          : __('Are you sure you want to remove this payment?'),
      onConfirm: async () => {
        await handleDelete()
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  async function handleDelete() {
    setIsLoading(true)
    const payments = getPayments({ quote: fullDoc }).filter((_, index) => index !== paymentIndex)
    const updatedQuote = setPayments({ quote: fullDoc, payments, options })
    if (onDeal) {
      const { quote: savedQuote } = await saveQuote({
        quote: {
          ...updatedQuote,
          dates: {
            ...updatedQuote.dates,
            updated: Date.now()
          }
        }
      })
      onConfirm(savedQuote)
    } else {
      updateQuote({ quote: updatedQuote, autosave: true })
      if (onConfirm) {
        onConfirm(updatedQuote)
      }
    }
    setIsLoading(false)
  }

  async function handleConfirm() {
    // Require a payment method whenever the Type/Method dropdown is shown — i.e. for non-request
    // payments, and when marking a request as paid (where isRequest is still true).
    const typeInvalid = (!isRequest || !!markingPaid) && !payment.type && (markingPaid || payment.status === 'paid')
    const amountInvalid = isAmountInvalid(payment.amount, allowNegativeAmount)
    if (typeInvalid || amountInvalid) {
      setValidationError(typeInvalid)
      setAmountError(amountInvalid)
    } else if (onSaveIntercept) {
      onSaveIntercept()
    } else {
      setIsLoading(true)
      console.log({ fullDoc })
      const payments = getPayments({ quote: fullDoc })
      // != null is intentional, catches both null and undefined
      if (paymentIndex != null) {
        payments[paymentIndex] = {
          ...payment,
          // payment.amount is typed `number`, but at runtime can be a formatted input string
          // ("1,000.00"). parseNumber strips separators and passes numbers through unchanged.
          amount: parseNumber(payment.amount),
          status: markingPaid ? 'paid' : payment.status,
          note: payment.note || '',
          date: payment.date
        }
      } else {
        payments.push({
          ...payment,
          amount: parseNumber(payment.amount)
        })
      }

      console.log({ payments })
      const updatedQuote = setPayments({ quote: fullDoc, payments, options })
      console.log({ updatedQuote })
      if (onDeal) {
        const { quote: savedQuote } = await saveQuote({
          quote: {
            ...updatedQuote,
            dates: {
              ...updatedQuote.dates,
              updated: Date.now()
            }
          }
        })

        setIsLoading(false)
        onConfirm(savedQuote)
        return
      } else {
        const { quote: savedQuote } = await saveQuote({
          quote: {
            ...updatedQuote,
            dates: {
              ...updatedQuote.dates,
              updated: Date.now()
            }
          }
        })
        const finalQuote = savedQuote ?? updatedQuote
        updateQuote({ quote: finalQuote, autosave: false, blockDirty: true })
        if (onConfirm) {
          onConfirm(finalQuote)
        } else {
          setIsLoading(false)
        }
      }
    }
  }
}

export default PaymentsDialog
